import React, { useState, useEffect } from 'react';
import clinicData from './data/clinicData.json';
import Appointment from './components/Appointment';
import AppointmentOnline from './components/AppointmentOnline';
import AppointmentOffline from './components/AppointmentOffline';
import SEOHelmet from './components/SEOHelmet';

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const testimonialsPerSlide = 3;
  const totalSlides = Math.ceil(clinicData.testimonials.length / testimonialsPerSlide);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => prev < totalSlides - 1 ? prev + 1 : 0);
    }, 20000);
    return () => clearInterval(interval);
  }, [totalSlides]);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (currentPath === '/appointment') {
    return <Appointment />;
  }
  if (currentPath === '/appointment/online') {
    return <AppointmentOnline />;
  }
  if (currentPath === '/appointment/offline') {
    return <AppointmentOffline />;
  }

  const handleWhatsApp = () => { 
    window.open(`https://wa.me/91${clinicData.contact.phone}?text=${clinicData.contact.message}`, '_blank');
  };

  const handleCall = () => {
    window.open(`tel:+91${clinicData.contact.phone}`, '_self');
  };

  const handleAppointment = () => {
    window.location.href = '/appointment';
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 64;
      const elementPosition = element.offsetTop - navbarHeight;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHelmet 
        title="Shriram Homoeopathic Clinic - Best Homeopathy & Counseling in Ravet, Pune"
        description="Expert homoeopathic treatment & psychological counseling in Ravet, Pune. Specialized care for children, families & emotional wellness. Call 7798702512 for appointment."
        keywords="homoeopathic clinic Pune, homeopathy Ravet, psychological counseling Pune, children healthcare, emotional wellness, family doctor Pune, natural healing, holistic treatment"
        canonicalUrl="https://shriramclinic.web.app/"
      />
      {/* Navbar */}
      <nav className="bg-white shadow-md fixed w-full top-0 z-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src="/images/caduceus.png" alt="Shriram Homoeopathic Clinic Logo - Medical Symbol" className="w-8 h-8 mr-3" />
              <h1 className="text-base font-bold text-forest leading-tight">
                <div>Shriram Homoeopathic Clinic</div>
                <div className="text-sm">& Psychological Counseling Centre</div>
              </h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-6 items-center">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-forest">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-forest">About</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-forest">Services</button>
              <button onClick={() => scrollToSection('doctor')} className="text-gray-700 hover:text-forest">Doctor</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-forest">Contact</button>
              <button onClick={handleAppointment} className="bg-forest text-white px-4 py-2 rounded-lg text-sm hover:bg-olive font-medium">Book Appointment</button>
              <button onClick={handleCall} className="bg-olive text-white px-3 py-2 rounded-lg text-sm hover:bg-forest">Call Now</button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-4 relative z-50 flex items-center justify-center mr-2"
            >
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span className={`block w-5 h-0.5 bg-gray-600 transition-all ${isMenuOpen ? 'rotate-45 translate-y-1' : ''}`}></span>
                <span className={`block w-5 h-0.5 bg-gray-600 mt-1 transition-all ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                <span className={`block w-5 h-0.5 bg-gray-600 mt-1 transition-all ${isMenuOpen ? '-rotate-45 -translate-y-1' : ''}`}></span>
              </div>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden bg-white border-t">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <button onClick={() => scrollToSection('home')} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">Home</button>
                <button onClick={() => scrollToSection('about')} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">About</button>
                <button onClick={() => scrollToSection('services')} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">Services</button>
                <button onClick={() => scrollToSection('doctor')} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">Doctor</button>
                <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">Contact</button>
                <button onClick={handleAppointment} className="block w-full text-left px-3 py-2 text-forest font-medium">Book Appointment</button>
                <button onClick={handleCall} className="block w-full text-left px-3 py-2 text-olive font-medium">Call Now</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className={`bg-gradient-to-br from-cream via-white to-sage min-h-screen flex ${isMobile ? 'items-start pt-40' : 'items-center'} px-4 relative overflow-hidden`} style={{backgroundImage: `url(/images/${isMobile ? 'background2' : 'background1'}.png)`, backgroundSize: 'cover', backgroundPosition: isMobile ? 'top' : 'center', backgroundRepeat: 'no-repeat'}}>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse-slow"></div>
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className={`${isMobile ? 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl' : 'text-xl md:text-3xl lg:text-4xl xl:text-5xl'} font-bold ${isMobile ? 'text-white' : 'text-gray-800'} mb-1 ${isMobile ? 'leading-tight' : 'leading-tight whitespace-nowrap'} animate-fadeInUp`}>
            {clinicData.hero.header}
          </h1>
          <p className={`${isMobile ? 'text-xl sm:text-2xl md:text-3xl lg:text-4xl' : 'text-lg md:text-2xl lg:text-3xl xl:text-4xl'} font-medium ${isMobile ? 'text-white' : 'text-gray-600'} mb-4 sm:mb-6 ${isMobile ? 'leading-tight' : 'leading-tight whitespace-nowrap'} animate-fadeInUp`} style={{animationDelay: '0.05s'}}>
            {clinicData.hero.subheader}
          </p>
          <h2 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold ${isMobile ? 'text-white' : 'text-gray-700'} mb-4 sm:mb-6 leading-tight animate-fadeInUp`} style={{animationDelay: '0.1s'}}>
            {clinicData.hero.headline}
          </h2>
          <p className={`text-lg sm:text-xl md:text-2xl ${isMobile ? 'text-white' : 'text-gray-600'} mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed animate-fadeInUp`} style={{animationDelay: '0.2s'}}>
            {clinicData.hero.subheadline}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8 px-4 animate-fadeInUp" style={{animationDelay: '0.4s'}}>
            <button onClick={handleAppointment} className="btn-primary w-full sm:w-auto animate-float">
              Book Appointment
            </button>
            <button onClick={handleCall} className="btn-secondary w-full sm:w-auto animate-float" style={{animationDelay: '1s'}}>
              Call Now
            </button>
          </div>
          <p className={`${isMobile ? 'text-white' : 'text-gray-500'} text-base sm:text-lg animate-fadeInUp`} style={{animationDelay: '0.6s'}}>
            {clinicData.hero.trustLine}
          </p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6">{clinicData.about.title}</h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-3 sm:mb-4">{clinicData.about.subtitle}</p>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            {clinicData.about.description}
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 sm:py-16 px-4 pb-24 sm:pb-28 bg-gradient-to-br from-cream to-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center gradient-text mb-4 leading-tight pb-2">{clinicData.services.title}</h2>
          <p className="text-lg sm:text-xl text-gray-600 text-center mb-8 sm:mb-12">{clinicData.services.subtitle}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {clinicData.services.items.map((service, index) => {
              const icons = ['local_pharmacy', 'psychology', 'child_care', 'family_restroom'];
              return (
              <div key={index} className="bg-white p-5 sm:p-6 rounded-xl shadow-lg card-hover border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-br from-forest to-olive rounded-full flex items-center justify-center mb-4 mx-auto">
                  <span className="material-icons text-white text-xl">{icons[index]}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 sm:mb-3 text-center">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 text-center">{service.description}</p>
              </div>
            );
            })}
          </div>
        </div>
      </section>

      {/* Children & Emotional Health */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6">{clinicData.childrenCare.title}</h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8">{clinicData.childrenCare.subtitle}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {clinicData.childrenCare.items.map((item, index) => (
              <div key={index} className="bg-sage p-3 sm:p-4 rounded-lg">
                <p className="text-sm sm:text-base text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 px-4 pb-24 sm:pb-28 bg-gradient-to-br from-sage to-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-8 sm:mb-12 leading-tight pb-2">{clinicData.whyChooseUs.title}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {clinicData.whyChooseUs.reasons.map((reason, index) => {
              const icons = ['favorite', 'local_hospital', 'eco', 'psychology', 'star', 'family_restroom'];
              return (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-white to-cream border-2 border-olive rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg animate-float" style={{animationDelay: `${index * 0.5}s`}}>
                    <span className="material-icons text-olive text-lg sm:text-2xl">{icons[index]}</span>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 font-medium">{reason}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Doctor Section */}
      <section id="doctor" className="py-12 sm:py-16 px-4 bg-gradient-to-br from-white to-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-6 sm:mb-8">Meet the Doctor</h2>
          <div className="glass-effect p-6 sm:p-8 rounded-2xl shadow-2xl">
            <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8">
              <div className="flex-shrink-0">
                <div className="relative">
                  <img 
                    src={clinicData.doctor.photo} 
                    alt="Dr. Shriram - Homoeopathic Doctor and Psychological Counselor in Ravet Pune"
                    className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-full object-cover shadow-2xl border-4 border-white"
                  />
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-transparent to-forest/20"></div>
                </div>
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">{clinicData.doctor.name}</h3>
                <p className="text-forest font-semibold mb-3 sm:mb-4 text-lg">{clinicData.doctor.qualifications}</p>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  {clinicData.doctor.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-16 px-4 pb-24 sm:pb-28 bg-gradient-to-br from-cream to-sage">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center gradient-text mb-8 sm:mb-12 leading-tight pb-2">What Families Say</h2>
          <div className="relative">
            <div className="overflow-hidden pt-4">
              <div className="flex transition-transform duration-500 ease-in-out" style={{transform: `translateX(-${currentSlide * 100}%)`}}>
                {Array.from({length: totalSlides}).map((_, slideIndex) => (
                  <div key={slideIndex} className="w-full flex-shrink-0">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 px-2">
                      {clinicData.testimonials.slice(slideIndex * testimonialsPerSlide, (slideIndex + 1) * testimonialsPerSlide).map((testimonial, index) => (
                        <div key={slideIndex * testimonialsPerSlide + index} className="bg-white/90 backdrop-blur-sm p-5 sm:p-6 rounded-xl relative border border-white/50">
                          <div className="absolute -top-2 -left-2 w-8 h-8 bg-gradient-to-br from-olive to-forest rounded-full flex items-center justify-center shadow-lg">
                            <span className="material-icons text-white text-lg">emoji_emotions</span>
                          </div>
                          <p className="text-sm sm:text-base text-gray-700 italic pt-4 leading-relaxed">{testimonial}</p>
                          <div className="flex justify-end mt-4">
                            {[...Array(5)].map((_, i) => (
                              <span key={i} className="text-olive text-sm">⭐</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {totalSlides > 1 && (
              <div className="flex justify-center items-center mt-6 gap-4">
                <button onClick={() => setCurrentSlide(currentSlide > 0 ? currentSlide - 1 : totalSlides - 1)} className="bg-white/80 hover:bg-white text-forest p-2 rounded-full shadow-lg">
                  <span className="material-icons">chevron_left</span>
                </button>
                <div className="flex gap-2">
                  {Array.from({length: totalSlides}).map((_, index) => (
                    <button key={index} onClick={() => setCurrentSlide(index)} className={`w-3 h-3 rounded-full transition-all ${currentSlide === index ? 'bg-forest' : 'bg-gray-300'}`}></button>
                  ))}
                </div>
                <button onClick={() => setCurrentSlide(currentSlide < totalSlides - 1 ? currentSlide + 1 : 0)} className="bg-white/80 hover:bg-white text-forest p-2 rounded-full shadow-lg">
                  <span className="material-icons">chevron_right</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 px-4 pb-24 sm:pb-28 bg-gradient-to-br from-white to-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-8 sm:mb-12 leading-tight pb-2">{clinicData.howItWorks.title}</h2>
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {clinicData.howItWorks.steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-forest to-olive rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg animate-pulse-slow" style={{animationDelay: `${index * 0.5}s`}}>
                    <span className="text-white text-lg sm:text-2xl font-bold">{index + 1}</span>
                  </div>
                  {index < 2 && (
                    <div className="hidden md:block absolute top-6 left-full w-full h-0.5 bg-gradient-to-r from-forest to-olive opacity-30"></div>
                  )}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">{step.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 sm:py-16 px-4 pb-24 sm:pb-28 bg-gradient-to-br from-sage to-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center gradient-text mb-8 sm:mb-12 leading-tight pb-2">Frequently Asked Questions</h2>
          <div className="space-y-6 sm:space-y-8">
            {clinicData.faqs.map((faq, index) => (
              <div key={index} className="bg-white/90 backdrop-blur-sm p-5 sm:p-6 rounded-xl shadow-lg card-hover border border-white/50">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-gradient-to-br from-forest to-olive rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm font-bold">?</span>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">{faq.question}</h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 sm:py-16 px-4 bg-forest text-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12">Contact & Location</h2>
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">{clinicData.contact.clinicName}</h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="flex items-start text-sm sm:text-base">
                  <span className="material-icons text-sage mr-2 mt-1">location_on</span>
                  <span>{clinicData.contact.address}</span>
                </p>
                <p className="flex items-center text-sm sm:text-base">
                  <span className="material-icons text-sage mr-2">phone</span>
                  <a href={`tel:+91${clinicData.contact.phone}`} className="hover:text-sage">{clinicData.contact.phone}</a>
                </p>
                <p className="flex items-center text-sm sm:text-base">
                  <span className="material-icons text-sage mr-2">chat</span>
                  <a href={`https://wa.me/91${clinicData.contact.phone}`} className="hover:text-sage">WhatsApp: {clinicData.contact.phone}</a>
                </p>
                <p className="flex items-center text-sm sm:text-base">
                  <span className="material-icons text-sage mr-2">email</span>
                  <a href={`mailto:${clinicData.contact.email}`} className="hover:text-sage break-all">{clinicData.contact.email}</a>
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
                  <span className="material-icons text-sage mr-2 align-middle">access_time</span>
                  Clinic Timings
                </h3>
                <div className="space-y-2 text-sm sm:text-base">
                  <p>{clinicData.contact.timings.weekdays}</p>
                  <p>{clinicData.contact.timings.evening}</p>
                  <p>{clinicData.contact.timings.sunday}</p>
                </div>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
                  <span className="material-icons text-sage mr-2 align-middle">location_on</span>
                  Location
                </h3>
                <iframe 
                  src={clinicData.contact.mapUrl}
                  width="100%" 
                  height={isMobile ? "150" : "200"} 
                  style={{border:0}} 
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-lg"
                ></iframe>
              </div>
            </div>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 md:col-span-2">
              <button onClick={handleAppointment} className="bg-white text-forest px-6 py-3 rounded-lg font-medium hover:bg-sage hover:text-white transition-all duration-200 w-full sm:w-auto flex items-center justify-center gap-2">
                <span className="material-icons text-lg">event</span>
                Book Appointment
              </button>
              <button onClick={handleCall} className="bg-white text-forest px-6 py-3 rounded-lg font-medium hover:bg-sage hover:text-white transition-all duration-200 w-full sm:w-auto flex items-center justify-center gap-2">
                <span className="material-icons text-lg">phone</span>
                Call Now
              </button>
              <button onClick={handleWhatsApp} className="bg-white text-forest px-6 py-3 rounded-lg font-medium hover:bg-sage hover:text-white transition-all duration-200 w-full sm:w-auto flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"/>
                </svg>
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-6 sm:py-8 px-4 text-center">
        <p className="text-sm sm:text-base">&copy; 2024 Shriram Homoeopathic Clinic. All rights reserved.</p>
        <p className="mt-2 text-sm sm:text-base">Gentle Healing, Deep Care - Serving families since 2011</p>
      </footer>

      {/* WhatsApp Float Button */}
      <a 
        href={`https://wa.me/91${clinicData.contact.phone}?text=${clinicData.contact.message}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 animate-float"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"/>
        </svg>
      </a>
    </div>
  );
};

export default App;